import CallTypes from 'store/call/call.types';
import * as callActions from 'store/call/call.actions';
import { parkCallContact } from 'store/contact/contact.actions';
import * as userActions from 'store/user/user.actions';
import * as appActions from 'store/app/app.actions';
import * as chatActions from 'store/chat/chat.actions';
import * as taskActions from 'store/tasks/tasks.actions';

import { Middleware } from 'redux';
import RootState from 'store/state';
import { updateContactAttributes, setContact } from 'store/contact/contact.actions';
import { addContact } from 'store/contacts/contacts.actions';
import { addError, setRedirect } from 'store/global/global.actions';
import AnyAction from 'store/anyActions';
import { monitorConnection, getContactState } from './ccp.utils';
import ChatTypes from 'store/chat/chat.types';
import { ChatMessageEvent, IChatAttachmentFile } from 'store/chat/chat.state';
import AppTypes from 'store/app/app.types';
import listenToContact from './listeners/listeners.contact';
import { batch } from 'react-redux';
import { MessageSources } from 'store/chat/chat.utils';
import PhoneLib from 'google-libphonenumber';
import TaskTypes from 'store/tasks/tasks.types';
import { debouncer, matchPattern, formatPhoneNumber, getNumberToDialThroughConnect } from 'utils';
import { sipGateway } from 'services/api/api.sipGateway';
import { isValidNumberForRegion } from 'libphonenumber-js';
import { sendEvent } from 'analytics/GoogleAnalytics';
import { SocialChatOutboundMessage } from '@missionlabs/smartagent-service-chat/dist/types/socialChatMessage';
import { postSocialChatMessage } from 'services/api/api.chat';
import { getConnectChatAttachmentUrl } from 'services/api/api.contact';
import { playDTMFTone } from './ccp.dtmf';
import { sendWhatsAppReadReceipt } from 'services/api/api.social';
import { SAMessageType } from '@missionlabs/smartagent-chat-components-lib/dist/ChatMessage/chat-message.types';
import { updateConnectionEditorContent } from 'store/chat/chat.actions';
import { ContentState, EditorState } from 'draft-js';
import { WebSocketCompanionActions } from 'store/websocketCompanion/websocketCompanion.types';

// eslint-disable-next-line
let getChatContact: (id: string) => connect.Contact;
let getTaskContact: (id: string) => connect.Contact;

let getContact: () => connect.Contact;

const getConnection = (connectionId: string) => {
    return getContact()
        .getConnections()
        .find(c => c.getConnectionId() === connectionId);
};

// replace all instances of '_' with '*' due to markdown rendering issues; escape the underscore if the content is an email
const convertMessage = (msg: string) => {
    const emails = msg.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/gi);
    msg = msg.replaceAll('_', '*');

    emails?.forEach(x => {
        msg = msg.replace(x.replace('_', '*'), x.replace('_', '\\_'));
    });
    return msg;
};

const getChatController = (id: string) =>
    (getChatContact(id).getAgentConnection() as connect.ChatConnection).getMediaController();

const ccpContactMiddleware: Middleware<{}, RootState> = store => next => {
    const sendTypingEvent = debouncer({
        onCall: (contactId: string) => {
            const connection = getChatContact(contactId).getAgentConnection() as connect.ChatConnection;

            connection.getMediaController().then(controller =>
                controller.sendEvent({
                    content: null,
                    contentType: ChatMessageEvent.TYPING,
                })
            );
        },
    });

    const ID = store.getState().app.ID;

    return async (action: AnyAction) => {
        if (store.getState().app?.callProvider === 'kumodi') {
            return next(action);
        }

        const { call, websocketCompanion } = store.getState();

        switch (action.type) {
            case AppTypes.SELECT_INSTANCE:
                [getContact, getChatContact, getTaskContact] = listenToContact(store);
                return next(action);
            case CallTypes.ACCEPT_CALL:
                console.log('accepting call');
                if (getContact().isConnected()) {
                    console.log('contact already in state of answering call');
                    return next(action);
                }
                getContact().accept({
                    success: () => {
                        store.dispatch(userActions.getQuickConnects());
                        return next(action);
                    },
                    failure: (error: string) => {
                        console.error(new Error('Error at ACCEPT_CALL'));
                        websocketCompanion.client?.sendMessage(WebSocketCompanionActions.ON_CALL_ANSWERED_FAILURE, {
                            number: call?.number,
                        });

                        store.dispatch(
                            addError(
                                JSON.parse(error).message || 'Oops, there was an error while trying to connect the call'
                            )
                        );
                    },
                });
                return;
            case CallTypes.REJECT_CALL:
                getContact()
                    .getInitialConnection()
                    .destroy({
                        success: () => {
                            websocketCompanion.client?.sendMessage(WebSocketCompanionActions.ON_CALL_REJECTED_SUCCESS, {
                                number: call?.number,
                            });
                        },
                        failure: () => {
                            websocketCompanion.client?.sendMessage(WebSocketCompanionActions.ON_CALL_REJECTED_FAILURE, {
                                number: call?.number,
                            });
                        }
                    });
                return next(action);

            case CallTypes.ADD_CONNECTION: {
                const {
                    app: { appConfig },
                    auth: { token },
                } = store.getState();

                let number = action.payload.number?.replace(/\s/g, '');

                const gatewayConfig = appConfig?.SIPgatewayConfig;
                const isGatewayNumber = gatewayConfig?.conditions.some(v => {
                    return matchPattern(v.regexPattern, number);
                });
                const countryCodeExclusion = gatewayConfig?.exclusions?.countryCodes?.some(countryCode => {
                    return isValidNumberForRegion(number!, countryCode);
                });
                const regexExclusion = gatewayConfig?.exclusions?.regexPatterns?.some(regexPattern => {
                    return matchPattern(regexPattern, number);
                });
                console.log('TRANSSFER: CountryCodeExclusion', countryCodeExclusion);
                console.log('TRANSFER: regexExclusion', regexExclusion);
                if (isGatewayNumber && (!countryCodeExclusion || number?.length === 7) && !regexExclusion) {
                    console.log(`TRANSFER: number ${number} hits sip gateway`);
                    const data = await sipGateway(gatewayConfig!, number!, token!);
                    number = `+${data.gatewayE164}`;
                } else {
                    console.log(`TRANSFER: number ${number} hits default call logic`);
                }

                if (number) {
                    const countryAbbreviation = store.getState().app.appConfig?.defaultCountryCode;

                    const { formattedNumber, countryCode } = getNumberToDialThroughConnect(number, countryAbbreviation);

                    number = formatPhoneNumber(formattedNumber, PhoneLib.PhoneNumberFormat.E164, countryCode);
                }

                const new_endpoint = number
                    ? connect.Endpoint.byPhoneNumber(number)
                    : (action.payload.endpoint as connect.Endpoint);
                console.log('ENDPOINT', new_endpoint);
                getContact().addConnection(new_endpoint, {
                    success: () => {
                        //Monitor connection for changes as we don't get event listeners
                        let i = 0;
                        const interval = setInterval(() => {
                            if (i === 10) {
                                console.log('cant get third party connection!');
                                store.dispatch(addError('Unable to transfer call'));
                                store.dispatch(callActions.endConnection(action.payload.id));
                                clearInterval(interval);
                            }
                            i++;
                            const connection =
                                getContact().getSingleActiveThirdPartyConnection() as connect.VoiceConnection;
                            if (!connection) return;
                            next({
                                ...action,
                                payload: {
                                    ...action.payload,
                                    destinationID: action.payload.endpoint
                                        ? action.payload.endpoint.endpointId
                                        : undefined,
                                    id: connection.getConnectionId(),
                                },
                            });
                            clearInterval(interval);
                            monitorConnection(store, connection);
                        }, 500);
                        return;
                    },
                    failure: (error: string) => {
                        console.error(new Error('Error at ADD_CONNECTION'));

                        store.dispatch(
                            addError(
                                JSON.parse(error).message || 'Oops, there was an error while trying to connect the call'
                            )
                        );
                        console.log(error);
                    },
                });
                return;
            }
            case CallTypes.TRANSFER_CALL: {
                store.dispatch(
                    callActions.addConnection(action.payload.number!, action.payload.name, action.payload.endpoint)
                );
                return next(action);
            }
            case CallTypes.HOLD: {
                const connection = getConnection(action.payload);
                if (!connection) return;

                if (connection.isOnHold()) {
                    return next(action);
                }

                console.log('TRYING TO HOLD', connection);
                connection.hold({
                    success: () => {
                        next(action);
                        //Need to mute agent if putting customer on hold and config is set
                        if (connection.isInitialConnection() && store.getState().app.appConfig.muteOnHold) {
                            store.dispatch(callActions.mute());
                        }

                        websocketCompanion.client?.sendMessage(WebSocketCompanionActions.ON_CALL_HOLD_SUCCESS, {
                            connectionID: connection.connectionId,
                        });
                    },
                    failure: (error: string) => {
                        console.error(new Error('Error at HOLD'));

                        store.dispatch(
                            addError(
                                JSON.parse(error).message ||
                                    'Oops, there was an error while trying to put the call on hold'
                            )
                        );
                        console.log(error);

                        websocketCompanion.client?.sendMessage(WebSocketCompanionActions.ON_CALL_HOLD_FAILURE, {
                            connectionID: connection.connectionId,
                        });
                    },
                });
                return;
            }
            case CallTypes.RESUME: {
                const connection = getConnection(action.payload);
                if (!connection) return;
                if (!connection.isOnHold()) {
                    return next(action);
                }

                connection.resume({
                    success: () => {
                        next(action);
                        if (connection.isInitialConnection() && store.getState().app.appConfig.muteOnHold) {
                            store.dispatch(callActions.unmute());
                        }

                        websocketCompanion.client?.sendMessage(WebSocketCompanionActions.ON_CALL_RESUME_SUCCESS, {
                            connectionID: connection.connectionId,
                        });
                    },
                    failure: (error: string) => {
                        console.error(new Error('Error at RESUME'));
                        store.dispatch(
                            addError(
                                JSON.parse(error).message || 'Oops, there was an error while trying to resume the call'
                            )
                        );
                        console.log(error);

                        websocketCompanion.client?.sendMessage(WebSocketCompanionActions.ON_CALL_RESUME_FAILURE, {
                            connectionID: connection.connectionId,
                        });
                    },
                });
                return;
            }
            case CallTypes.SET_SELECTED_CALL: {
                try {
                    const callContact = getContact();
                    if (!callContact) return;
                    const contactState = getContactState(store.getState(), callContact);
                    batch(() => {
                        store.dispatch(setContact(contactState));
                        store.dispatch(addContact(contactState));
                    });
                } catch (error) {
                    console.error('Error during setSelectedCall', error);
                }
                return next(action);
            }
            case CallTypes.END_CALL: {
                getContact()
                    .getConnections()
					.forEach(c => c.isActive() ? c.destroy({
                        success: () => {
                            websocketCompanion.client?.sendMessage(WebSocketCompanionActions.ON_CALL_END_SUCCESS);
                        },
                        failure: () => {
                            websocketCompanion.client?.sendMessage(WebSocketCompanionActions.ON_CALL_END_FAILURE);
                        },
                    }) : null);

                return store.dispatch(callActions.callEnded());
            }
            case CallTypes.UPDATE_MONITOR_STATUS: {
                const { status } = action.payload;

                getContact().updateMonitorParticipantState(status as connect.MonitoringMode, {
                    success: function () {
                        store.dispatch(callActions.setMonitorStatus(status));
                    },
                    failure: function (error) {
                        console.error('An error occurred when updating the monitor status: ', error);
                    },
                });
                return next(action);
            }
            case CallTypes.CALL_ENDED: {
                const {
                    contact,
                    call,
                    user,
                    app: { appConfig },
                } = store.getState();

                const { monitorInfo = { status: undefined, isEnhancedMonitoringEnabled: false } } = call ?? {};
                const { status, isEnhancedMonitoringEnabled } = monitorInfo;

                //If agent is in monitoring then we need to just change their status back to what they were in
                if (status === 'SILENT_MONITOR' && !isEnhancedMonitoringEnabled) {
                    const state = user.states.find(s => s.name === user.status.name);

                    if (state) store.dispatch(userActions.setState(state));
                }

                if (!contact) return next(action);
                //If the agent hasn't hung up then set the contact attribute to no
                if (!contact.attributes['sa-agent-hungup']) {
                    store.dispatch(updateContactAttributes(contact.ID, { 'sa-agent-hungup': 'no' }));
                }
                //If auto generate report then send off
                if (appConfig.autoPostReportIssue) {
                    store.dispatch(
                        appActions.reportIssue(
                            `Auto generated report for contact ${contact?.ID ? contact?.ID : 'unknown'}`
                        )
                    );
                }
                return next(action);
            }
            case CallTypes.SEND_DTMF: {
                const connection = getConnection(action.payload.connectionId);
                if (!connection) return;
                connection.sendDigits(action.payload.digit, {
                    success: () => {
                        playDTMFTone(action.payload.digit);
                        next(action);
                    },
                    failure: (error: string) => {
                        console.error(new Error('Error at SEND_DTMF'));
                        store.dispatch(
                            addError(
                                JSON.parse(error).message ||
                                    'Oops, there was an error while trying to send the dtmf tone'
                            )
                        );
                        console.log(error);
                    },
                });
                return next(action);
            }

            case CallTypes.LEAVE_CALL:
                getContact()
                    .getAgentConnection()
                    .destroy({
                        success: () => {
                            return next(action);
                        },
                        failure: (error: string) => {
                            console.error(new Error('Error at LEAVE_CALL'));

                            store.dispatch(
                                addError(
                                    JSON.parse(error).message ||
                                        'Oops, there was an error while trying to leave the call'
                                )
                            );
                            console.log(error);
                        },
                    });
                break;
            case CallTypes.END_CONNECTION: {
                const connection = getConnection(action.payload);
                const activeConnections = getContact()
                    .getConnections()
                    .filter(c => c.isActive());
                const {
                    call,
                    app: { appConfig },
                } = store.getState();
                const initialConnection = call?.connections.find(c => c.initialConnection);

                //Check if the main connection has come off hold from the transfer flow
                if (!getContact().getInitialConnection().isOnHold() && initialConnection?.hold) {
                    store.dispatch(callActions.resume(initialConnection.id));
                }
                if (!connection || !connection.isActive()) {
                    websocketCompanion.client?.sendMessage(WebSocketCompanionActions.ON_CONNECTION_END_SUCCESS, {
                        number: call?.number,
                    });

                    next(action);
                    if (activeConnections.length === 0) {
                        store.dispatch(callActions.callEnded());
                    }

                    return;
                }

                //Adding a connection to the contact object using a quick connect ARN
                const transferToEndpoint = (endpoint: connect.Endpoint) => {
                    getContact().addConnection(endpoint, {
                        success: () => {
                            next(action);
                            getContact().getAgentConnection().destroy({});
                            if (activeConnections.length === 0) {
                                store.dispatch(callActions.callEnded());
                            }
                        },
                        failure: (error: string) => {
                            console.log('ERROR', error);
                            destroyCurrentConnection();
                        },
                    });
                };

                //Display errors on the console when they happen
                const endConnectionError = (error: string) => {
                    websocketCompanion.client?.sendMessage(WebSocketCompanionActions.ON_CONNECTION_END_FAILURE, {
                        number: call?.number,
                    });

                    console.error(new Error('Error at END_CONNECTION'));
                    store.dispatch(
                        addError(JSON.parse(error).message || 'Oops, there was an error while trying to end the call')
                    );
                };

                //Generic function to end the current connection
                const destroyCurrentConnection = () => {
                    connection.destroy({
                        success: () => {
                            websocketCompanion.client?.sendMessage(WebSocketCompanionActions.ON_CONNECTION_END_SUCCESS, {
                                number: call?.number,
                            });
                            
                            next(action);
                            console.log(activeConnections.length, 'ACTIVE CONNETIONS');
                            if (activeConnections.length === 0) {
                                store.dispatch(callActions.callEnded());
                            }
                        },
                        failure: (error: string) => {
                            endConnectionError(error);
                        },
                    });
                };

                // If onHangUp config is present and transfer is true (turned on) and the connection is not "INTERNAL-TRANSFER" or outbound
                if (
                    appConfig.onHangUp?.transfer &&
                    initialConnection?.activeConnection &&
                    (connection.getEndpoint().phoneNumber !== 'INTERNAL-TRANSFER' ||
                        connection.getType() !== 'outbound')
                ) {
                    const quickConnects = store.getState().user.connects;
                    const surveyEndpoint = quickConnects?.find(c => c.name === appConfig.onHangUp?.transferName)!;
                    // If there are more than 2 connections
                    if (activeConnections.length > 2) {
                        // If hanging up on the initial connection (customer) just destroy the agent connection and leave the customer with the other connection
                        if (
                            store.getState().call?.conferenced &&
                            initialConnection?.id === connection.getConnectionId()
                        ) {
                            return store.dispatch(callActions.leaveCall());
                        }

                        // Destroy the 2nd call first
                        activeConnections[2].destroy({
                            success: () => {
                                next(action);
                                // On success transfer the call to the survey and finish agent connection
                                transferToEndpoint(surveyEndpoint);
                            },
                            failure: (error: string) => {
                                endConnectionError(error);
                            },
                        });
                    } else {
                        // Checking the connection is the customer inbound call (this also catches the initial connection between Agent A / Agent B from Agent B side)
                        if (
                            connection.getType() === 'inbound' &&
                            initialConnection?.id === connection.getConnectionId()
                        ) {
                            transferToEndpoint(surveyEndpoint);
                        } else {
                            destroyCurrentConnection();
                        }
                    }
                    //If there is no transfer config or it's not the initialConnection just destroy the connection
                } else {
                    destroyCurrentConnection();
                }
                return;
            }
            case CallTypes.TOGGLE_CONNECTIONS:
                getContact().toggleActiveConnections({
                    success: () => next(action),
                    failure: (error: string) => {
                        console.error(new Error('Error at TOGGLE_CONNECTIONS'));

                        store.dispatch(
                            addError(
                                JSON.parse(error).message || 'Oops, there was an error while trying to switch calls'
                            )
                        );
                        console.log(error);
                    },
                });
                return;
            case CallTypes.CONFERENCE_CONNECTIONS:
                getContact().conferenceConnections({
                    success: () => {
                        const call = store.getState()?.call;
                        if (call?.parking) {
                            const initialContactId = getContact().getOriginalContactId();

                            store.dispatch<any>(parkCallContact(initialContactId));
                            store.dispatch(callActions.leaveCall());
                            return;
                        }

                        next(action);
                        //Is the connection set to autoHangup
                        const connection = call?.connections[1];
                        if (!connection) return;
                        const { disallowedConferenceDestinations, disallowConferencing } =
                            store.getState().app.appConfig;
                        if (disallowConferencing) {
                            store.dispatch(callActions.leaveCall());
                            return;
                        }

                        //4be243f5-342f-44a1-b749-c23e0eee8047
                        if (disallowedConferenceDestinations && connection.destinationID) {
                            const destinationID = connection.destinationID.split('/transfer-destination/')[1];
                            console.log('Checking %s', destinationID);
                            console.log('Against ', disallowedConferenceDestinations);
                            if (disallowedConferenceDestinations.indexOf(destinationID) > -1) {
                                store.dispatch(callActions.leaveCall());
                            }
                        }
                        return;
                    },
                    failure: (error: string) => {
                        console.error(new Error('Error at CONFERENCE_CONNECTIONS'));
                        store.dispatch(
                            addError(
                                JSON.parse(error).message ||
                                    'Oops, there was an error while trying to conference the call'
                            )
                        );
                        console.log(error);
                    },
                });
                return;
            case CallTypes.CONNECTION_STARTED: {
                next(action);

                if (store.getState().call?.parking) {
                    store.dispatch(callActions.conferenceConnections());
                }

                const connection = getConnection(action.payload);

                if (connection?.isInitialConnection()) {
                    websocketCompanion.client?.sendMessage(WebSocketCompanionActions.ON_CALL_CONNECTED_SUCCESS, {
                        contactConnectedTimestamp: new Date().toISOString(),
                    });
                }

                //Need to unmute agent if transferring and they've been put on mute
                if (!connection?.isInitialConnection() && store.getState().app.appConfig.muteOnHold) {
                    store.dispatch(callActions.unmute());
                }
                return;
            }
            case CallTypes.COMPLETE_CALL_ACW: {
                store.dispatch(userActions.afterCallWorkEnd());
                getContact().clear({
                    failure: (error: any) => console.error('error completing contact', error),
                });
                return next(action);
            }
            case ChatTypes.ACCEPT_CHAT: {
                return getChatContact(action.payload).accept({
                    success: () => {
                        next(action);
                        sendEvent({
                            eventName: 'contact_received',
                            eventParams: {
                                company_name: ID,
                                environment: process.env.REACT_APP_ENV,
                                channel: 'CHAT',
                            },
                        });
                    },
                    failure: () => console.log('failed to accept chat with contactID: ' + action.payload),
                });
            }
            case ChatTypes.DECLINE_CHAT: {
                return getChatContact(action.payload)
                    .getInitialConnection()
                    .destroy({
                        success: () => next(action),
                        failure: () => {
                            console.log('Error declining chat');
                            next(action);
                        },
                    });
            }
            case ChatTypes.SEND_CHAT_MESSAGE: {
                const contact = getChatContact(action.payload.id);
                const connection = contact.getAgentConnection() as connect.ChatConnection;
                const message = action.payload.msg;
                const attributes = contact.getAttributes();
                const socialMediaPlatform = attributes['sa-social-media-platform'];
                // make call to service chat
                if (socialMediaPlatform) {
                    const socialChatMessage: SocialChatOutboundMessage = {
                        content: message,
                        channel: attributes['sa-social-media-platform'].value,
                        subChannel: attributes['sa-sub-channel'].value,
                        systemEndpointID: attributes['sa-system-endpoint-id'].value,
                        systemEndpointAddress: attributes['sa-system-endpoint-address'].value,
                        customerEndpointID: attributes['sa-customer-endpoint-id'].value,
                        customerEndpointAddress: attributes['sa-customer-endpoint-address'].value,
                    };

                    await postSocialChatMessage(socialChatMessage);
                }

                if (!message.trim()) {
                    return next(action);
                }

                return connection
                    .getMediaController()
                    .then(async controller => {
                        const sendMessageResult = await controller.sendMessage({
                            message: message.startsWith('{"') ? message : convertMessage(message),
                            contentType: 'text/plain',
                        });
                    })
                    .then(_ => next(action));
            }
            case ChatTypes.RECIEVE_CHAT_MESSAGE: {
                const messages = store.getState().chat.connections.find(c => c.id === action.payload.id)?.messages;
                const lastMessage = messages?.slice(-1).pop();

                if (!lastMessage || lastMessage.Id !== action.payload.msg.Id) {
                    return next(action);
                }

                return;
            }
            case ChatTypes.SET_SELECTED_CHAT:
                batch(() => {
                    if (!action.payload) {
                        return store.dispatch(setContact(null));
                    }
                    const { contacts } = store.getState();
                    const contactState =
                        contacts.find(contact => contact.ID === action.payload) ??
                        getContactState(store.getState(), getChatContact(action.payload ?? ''));
                    store.dispatch(setContact(contactState));
                    store.dispatch(addContact(contactState));

                    const attributes = contactState.attributes;
                    // Is email
                    if (attributes['sa-chat-channel']?.value === MessageSources.EMAIL.toString())
                        store.dispatch(setRedirect(`/case/${attributes['sa-case-id']?.value}`));
                    else store.dispatch(setRedirect('/chat'));
                });
                return next(action);
            case ChatTypes.END_CHAT:
                return getChatContact(action.payload).getAgentConnection().destroy({}); // dont destroy the contact itself or we lose ACW
            case ChatTypes.COMPLETE_CHAT_ACW: {
                store.dispatch(chatActions.clearChatConnection(action.payload));
                getChatContact(action.payload).clear({
                    failure: (error: any) => console.error('error completing contact', error),
                });
                return next(action);
            }
            case ChatTypes.REMOVE_CHAT_CONNECTION:
                try {
                    getChatContact(action.payload)
                        ?.getConnections()
                        .forEach(c =>
                            c.destroy({
                                success: (...info: any) => console.log('Chat connection destroyed successfully', info),
                                failure: (error: any) => console.error('Destroy chat connection error', error),
                            })
                        );
                    //This happens if the connection has been ended through the CCP so suppress the error
                } catch (e) {
                    console.error('Chat connection destroy error', e);
                }
                return next(action);
            case ChatTypes.CHAT_MISSED:
                if (store.getState().contact?.ID === action.payload) {
                    store.dispatch(setContact(null));
                }

                return next(action);
            case ChatTypes.CHAT_SEND_TYPING_EVENT:
                sendTypingEvent(action.payload.id);
                return next(action);

            case ChatTypes.SEND_ATTACHMENT:
                try {
                    const [controller, chatConnections, contact] = await Promise.all([
                        getChatController(action.payload.id),
                        store.getState().chat.connections.find(c => c.id === action.payload.id),
                        getChatContact(action.payload.id),
                    ]);

                    const chatAttachments = chatConnections?.attachments.files ?? [];

                    const sendAttachmentAsync = async (attachment: IChatAttachmentFile, index: number) => {
                        const response = await controller.sendAttachment({
                            attachment: attachment.file,
                        });
                        store.dispatch(chatActions.setAttachmentLoading(action.payload.id, 'done', index));
                        return {
                            attachmentID: response.request.params.AttachmentIds[0],
                            type: attachment.file.type,
                        };
                    };

                    const attachmentsData = await Promise.all(
                        chatAttachments.map((attachment, i) => sendAttachmentAsync(attachment, i))
                    );
                    const attachmentsToSend = [...attachmentsData];

                    const attributes = contact.getAttributes();
                    const socialMediaPlatform = attributes['sa-social-media-platform'];

                    store.dispatch(
                        updateConnectionEditorContent(
                            action.payload.id,
                            EditorState.createWithContent(ContentState.createFromText(''))
                        )
                    );

                    if (socialMediaPlatform.value === 'WHATSAPP') {
                        const socialChatMessage: SocialChatOutboundMessage = {
                            content: '',
                            attachmentsToSend,
                            channel: attributes['sa-social-media-platform'].value,
                            subChannel: attributes['sa-sub-channel'].value,
                            systemEndpointID: attributes['sa-system-endpoint-id'].value,
                            systemEndpointAddress: attributes['sa-system-endpoint-address'].value,
                            customerEndpointID: attributes['sa-customer-endpoint-id'].value,
                            customerEndpointAddress: attributes['sa-customer-endpoint-address'].value,
                        };

                        await postSocialChatMessage(socialChatMessage);
                    }

                    if (action.payload.stringMessage.replaceAll('\n', '')) {
                        store.dispatch(chatActions.sendChatMessage(action.payload.id, action.payload.stringMessage));
                    }

                    return next(action);
                } catch (err) {
                    console.log(err);
                    return next(action);
                }
            case ChatTypes.DOWNLOAD_ATTACHMENT:
                if (!store.getState().contact?.ID) {
                    return next(action);
                }

                const downloadAttachmentResponse: Blob = await (
                    getChatContact(
                        store.getState().contact?.ID as string
                    ).getAgentConnection() as connect.ChatConnection
                )
                    .getMediaController()
                    .then(controller =>
                        controller.downloadAttachment({
                            attachmentId: action.payload.attachmentId,
                        })
                    );
                const objectUrl = window.URL.createObjectURL(downloadAttachmentResponse);
                const link = document.createElement('a');
                link.href = objectUrl;
                link.download = action.payload.attachmentName;
                document.body.appendChild(link);
                link.dispatchEvent(
                    new MouseEvent('click', {
                        bubbles: true,
                        cancelable: true,
                        view: window,
                    })
                );
                document.body.removeChild(link);
                window.URL.revokeObjectURL(objectUrl);
                return next(action);
            case ChatTypes.DOWNLOAD_CONNECT_ATTACHMENT_FROM_TRANSCRIPT:
                const { attachmentID, contactID } = action.payload;

                const attachmentUrl = await getConnectChatAttachmentUrl(contactID, attachmentID);

                const anchorTag = document.createElement('a');
                anchorTag.href = attachmentUrl;
                anchorTag.target = '_blank';
                anchorTag.download = attachmentID;
                document.body.appendChild(anchorTag);
                anchorTag.click();
                document.body.removeChild(anchorTag);

                return next(action);

            case ChatTypes.TRANSFER_CHAT:
                getChatContact(action.payload.id).addConnection(action.payload.endpoint, {
                    success: console.log,
                    failure: console.log,
                });
                return next(action);
            case ChatTypes.CLEAR_UNREAD:
                const { messages } = store.getState().chat.connections.find(c => c.id === action.payload)!;
                const unreadCustomerMessages = messages.filter(
                    message =>
                        message.ParticipantRole === 'CUSTOMER' &&
                        ['text/markdown', 'text/plain'].includes(message.ContentType!) &&
                        !message.readTimestamp
                );
                if (!unreadCustomerMessages.length) {
                    return next(action);
                }

                // send read receipt if its a whatsapp message
                const lastUnreadMessageContent = unreadCustomerMessages[unreadCustomerMessages.length - 1]
                    .content as SAMessageType;
                if (
                    lastUnreadMessageContent?.externalMessageID?.startsWith('wamid') &&
                    lastUnreadMessageContent?.systemEndpointID
                ) {
                    const contact = store.getState().contact;
                    const systemEndpointAddress =
                        contact?.ID === action.payload ? contact.systemEndpointAddress : undefined;
                    if (systemEndpointAddress) {
                        sendWhatsAppReadReceipt({
                            messageID: lastUnreadMessageContent.externalMessageID,
                            whatsAppCustomerConfigID: systemEndpointAddress,
                            phoneNumberID: lastUnreadMessageContent.systemEndpointID,
                        });
                    }
                }

                const controller = await getChatController(action.payload);
                unreadCustomerMessages.forEach(message => {
                    controller.sendEvent({
                        content: JSON.stringify({ messageId: message.Id }),
                        contentType: ChatMessageEvent.MESSAGE_READ,
                    });
                });

                return next(action);

            case TaskTypes.ACCEPT_TASK: {
                return getTaskContact(action.payload).accept({
                    success: () => {
                        next(action);
                        sendEvent({
                            eventName: 'contact_received',
                            eventParams: {
                                company_name: ID,
                                environment: process.env.REACT_APP_ENV,
                                channel: 'TASK',
                            },
                        });

                        const tasks = store.getState().tasks?.connections;
                        const hasActiveTask = tasks.find(c => c.status === connect.ContactStateType.CONNECTED);

                        if (!hasActiveTask) {
                            store.dispatch(taskActions.setSelectedTask(action.payload));
                        }
                    },
                    failure: () => console.log('failed to accept task with contactID: ' + action.payload),
                });
            }

            case TaskTypes.DECLINE_TASK: {
                return getTaskContact(action.payload)
                    .getInitialConnection()
                    .destroy({
                        success: () => next(action),
                        failure: () => {
                            console.log('Error declining task');
                            next(action);
                        },
                    });
            }
            case TaskTypes.SET_SELECTED_TASK:
                batch(() => {
                    if (!action.payload) {
                        return store.dispatch(setContact(null));
                    }
                    const taskContact = getTaskContact(action.payload ?? '');
                    if (taskContact) {
                        const taskContactState = getContactState(store.getState(), taskContact);
                        store.dispatch(setContact(taskContactState));
                        store.dispatch(addContact(taskContactState));
                        store.dispatch(setRedirect(`/tasks`));
                    }
                });
                return next(action);
            case TaskTypes.END_TASK:
                return getTaskContact(action.payload).getAgentConnection().destroy({}); // dont destroy the contact itself or we lose ACW
            case TaskTypes.COMPLETE_TASK_ACW: {
                store.dispatch(taskActions.clearTaskConnection(action.payload));
                getTaskContact(action.payload).clear({
                    failure: (error: any) => console.error('error completing contact', error),
                });
                return next(action);
            }
            case TaskTypes.REMOVE_TASK_CONNECTION:
                try {
                    getTaskContact(action.payload)
                        ?.getConnections()
                        .forEach(c => c.destroy({}));
                } catch (ex) {
                    //This happens if the connection has been ended through the CCP so suppress the error
                    console.error('error removing connection', ex);
                }
                return next(action);
            case TaskTypes.TASK_MISSED:
                if (store.getState().contact?.ID === action.payload) {
                    store.dispatch(setContact(null));
                }
                return next(action);
            case TaskTypes.TRANSFER_TASK:
                getTaskContact(action.payload.id).addConnection(action.payload.endpoint, {
                    success: console.log,
                    failure: (err: any) => {
                        console.log('ERROR', err);
                        store.dispatch(addError(err.message || 'Error while transferring a task'));
                    },
                });
                return next(action);
            default:
                return next(action);
        }
    };
};

export default ccpContactMiddleware;
